import maplibregl from 'maplibre-gl'
import {
  pointToLineDistance,
  point as turfPoint,
  lineString,
  bboxPolygon,
  booleanDisjoint,
} from '@turf/turf'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'
import { OFM_PINNED_TILE_URL, OFM_TILEJSON_URL, OFM_MAXZOOM } from '../../lib/ofm-config.js'
import type { SelectableLayerProvider } from '../selection/highlight-adapter.js'
import type {
  SelectableFeatureRef,
  BackcountryTrailRef,
  BackcountryPOIRef,
} from '../selection/selectable-feature-ref.js'

const SOURCE_ID = 'backcountry-ofm-source'
// Highlight overlay for selected OSM features (geometry captured at select time).
const SELECTION_SOURCE_ID = 'backcountry-selection'
const SELECTION_LINE_LAYER = 'backcountry-selection-line'
const SELECTION_POINT_LAYER = 'backcountry-selection-point'
// In the OpenMapTiles schema the trail geometry (the `transportation` layer we
// click) carries no name; names live in this sibling source-layer.
const TRAIL_NAME_SOURCE_LAYER = 'transportation_name'
const GROUP_ID = 'backcountry'
const DEFAULT_OPACITY = 0.9

export type BackcountryFeatureType =
  | 'trails'
  | 'peaks'
  | 'shelters'
  | 'campsites'
  | 'water'
  | 'passes'
  | 'trailheads'
  | 'viewpoints'
  | 'facilities'
  | 'waterfalls'

interface FeatureTypeDef {
  layers: string[]
  addLayers: (svc: BackcountryOverlayService) => void
}

const TRAILS_LAYER = 'backcountry-trails'
const TRAIL_LABELS_LAYER = 'backcountry-trail-labels'
const PEAKS_LAYER = 'backcountry-peaks'
const PEAK_LABELS_LAYER = 'backcountry-peak-labels'
const SHELTERS_LAYER = 'backcountry-shelters'
const CAMPSITES_LAYER = 'backcountry-campsites'
const WATER_LAYER = 'backcountry-water'
const PASSES_LAYER = 'backcountry-passes'
const PASS_LABELS_LAYER = 'backcountry-pass-labels'
const TRAILHEADS_LAYER = 'backcountry-trailheads'
const VIEWPOINTS_LAYER = 'backcountry-viewpoints'
const FACILITIES_LAYER = 'backcountry-facilities'
const WATERFALLS_LAYER = 'backcountry-waterfalls'

export const BACKCOUNTRY_FEATURE_TYPES: BackcountryFeatureType[] = [
  'trails',
  'peaks',
  'passes',
  'shelters',
  'campsites',
  'water',
  'trailheads',
  'viewpoints',
  'facilities',
  'waterfalls',
]

function allLayerIds(): string[] {
  const ids: string[] = []
  for (const def of Object.values(FEATURE_DEFS)) {
    ids.push(...def.layers)
  }
  return ids
}

const FEATURE_DEFS: Record<BackcountryFeatureType, FeatureTypeDef> = {
  trails: {
    layers: [TRAILS_LAYER, TRAIL_LABELS_LAYER],
    addLayers: svc => {
      svc.addTrailLayers()
    },
  },
  peaks: {
    layers: [PEAKS_LAYER, PEAK_LABELS_LAYER],
    addLayers: svc => {
      svc.addPeakLayers()
    },
  },
  shelters: {
    layers: [SHELTERS_LAYER],
    addLayers: svc => {
      svc.addShelterLayers()
    },
  },
  campsites: {
    layers: [CAMPSITES_LAYER],
    addLayers: svc => {
      svc.addCampsiteLayers()
    },
  },
  water: {
    layers: [WATER_LAYER],
    addLayers: svc => {
      svc.addWaterLayers()
    },
  },
  passes: {
    layers: [PASSES_LAYER, PASS_LABELS_LAYER],
    addLayers: svc => {
      svc.addPassLayers()
    },
  },
  trailheads: {
    layers: [TRAILHEADS_LAYER],
    addLayers: svc => {
      svc.addTrailheadLayers()
    },
  },
  viewpoints: {
    layers: [VIEWPOINTS_LAYER],
    addLayers: svc => {
      svc.addViewpointLayers()
    },
  },
  facilities: {
    layers: [FACILITIES_LAYER],
    addLayers: svc => {
      svc.addFacilityLayers()
    },
  },
  waterfalls: {
    layers: [WATERFALLS_LAYER],
    addLayers: svc => {
      svc.addWaterfallLayers()
    },
  },
}

const BACKCOUNTRY_ICONS: Record<string, { icon: string; color: string }> = {
  peaks: { icon: 'landscape', color: '#FF6F00' },
  shelters: { icon: 'cabin', color: '#4CAF50' },
  campsites: { icon: 'camping', color: '#795548' },
  water: { icon: 'water_drop', color: '#2196F3' },
  passes: { icon: 'swap_vert', color: '#7C3AED' },
  trailheads: { icon: 'flag', color: '#0D9488' },
  viewpoints: { icon: 'visibility', color: '#0284C7' },
  facilities: { icon: 'info', color: '#6B7280' },
  waterfalls: { icon: 'waves', color: '#0891B2' },
}

function registerBackcountryIcon(
  map: maplibregl.Map,
  imageId: string,
  iconName: string,
  color: string
): Promise<void> {
  if (map.hasImage(imageId)) return Promise.resolve()

  const dpr = window.devicePixelRatio || 1
  const size = 28
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 2

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  ctx.stroke()

  return new Promise<void>(resolve => {
    if (!document.fonts.check('16px "Material Symbols Outlined"')) {
      document.fonts
        .load('16px "Material Symbols Outlined"')
        .then(() => drawIconAndRegister())
        .catch(() => drawIconAndRegister())
    } else {
      drawIconAndRegister()
    }

    function drawIconAndRegister(): void {
      ctx.font = '14px "Material Symbols Outlined"'
      ctx.fillStyle = '#ffffff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(iconName, cx, cy)

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      map.addImage(imageId, imageData, { pixelRatio: dpr })
      resolve()
    }
  })
}

class BackcountryOverlayService implements SelectableLayerProvider {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _enabledTypes = new Set<BackcountryFeatureType>(BACKCOUNTRY_FEATURE_TYPES)
  private iconsReady: Promise<void> = Promise.resolve()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  isTypeEnabled(type: BackcountryFeatureType): boolean {
    return this._enabledTypes.has(type)
  }

  get enabledTypes(): ReadonlySet<BackcountryFeatureType> {
    return this._enabledTypes
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true

    this.registerIcons()

    this.ensureSource()

    void this.iconsReady.then(() => {
      if (!this._enabled || !this.map) return
      for (const type of this._enabledTypes) {
        FEATURE_DEFS[type].addLayers(this)
      }
    })
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.removeAllLayers()
    this.removeSource()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  toggleType(type: BackcountryFeatureType): void {
    if (this._enabledTypes.has(type)) {
      this._enabledTypes.delete(type)
      if (this._enabled) this.removeTypeLayers(type)
    } else {
      this._enabledTypes.add(type)
      if (this._enabled) {
        this.ensureSource()
        void this.iconsReady.then(() => {
          if (!this._enabled || !this.map) return
          FEATURE_DEFS[type].addLayers(this)
        })
      }
    }
  }

  setEnabledTypes(types: BackcountryFeatureType[]): void {
    this._enabledTypes = new Set(types)
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map) return
    for (const id of allLayerIds()) {
      if (!this.map.getLayer(id)) continue
      const layer = this.map.getLayer(id)!
      if (layer.type === 'line') {
        this.map.setPaintProperty(id, 'line-opacity', this._opacity)
      } else if (layer.type === 'circle') {
        this.map.setPaintProperty(id, 'circle-opacity', this._opacity)
        this.map.setPaintProperty(id, 'circle-stroke-opacity', Math.min(this._opacity + 0.1, 1))
      } else if (layer.type === 'symbol') {
        this.map.setPaintProperty(id, 'text-opacity', this._opacity)
        this.map.setPaintProperty(id, 'icon-opacity', this._opacity)
      }
    }
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    this.removeAllLayers()
    this.removeSource()
    this.registerIcons()

    this.ensureSource()

    void this.iconsReady.then(() => {
      if (!this._enabled || !this.map) return
      for (const type of this._enabledTypes) {
        FEATURE_DEFS[type].addLayers(this)
      }
    })
  }

  /** Layer ids currently on the map that respond to identify (for hover cursor). */
  interactiveLayerIds(): string[] {
    if (!this.map || !this._enabled) return []
    return allLayerIds().filter(id => this.map!.getLayer(id))
  }

  // ── Selection (SelectableLayerProvider) ──────────────────────────────────
  // OSM features are selectable for building plan routes. Geometry (no stable
  // tile id) is captured into the ref and drives the highlight overlay below.

  /** Geometry layers (not the text-label layers) that selection queries. */
  private selectableLayerIds(): string[] {
    return allLayerIds().filter(id => !id.includes('-label'))
  }

  getSelectableLayerIds(): string[] {
    if (!this.map || !this._enabled) return []
    return this.selectableLayerIds().filter(id => this.map!.getLayer(id))
  }

  featureToRef(hit: maplibregl.MapGeoJSONFeature): SelectableFeatureRef | null {
    const geom = hit.geometry
    if (hit.layer.id === TRAILS_LAYER) {
      if (geom.type !== 'LineString' && geom.type !== 'MultiLineString') return null
      const name = (hit.properties.name as string | undefined) ?? ''
      return { source: 'backcountry-trail', name, geometry: geom }
    }
    // Every other selectable backcountry layer is a POI point.
    if (this.selectableLayerIds().includes(hit.layer.id)) {
      if (geom.type !== 'Point') return null
      const props = hit.properties
      const name = (props.name as string | undefined) ?? ''
      const subclass =
        (props.subclass as string | undefined) ?? (props.class as string | undefined) ?? ''
      return {
        source: 'backcountry-poi',
        name,
        subclass,
        coordinates: [geom.coordinates[0], geom.coordinates[1]],
        geometry: geom,
      }
    }
    return null
  }

  /**
   * OpenMapTiles merges OSM ways with identical tags into one MultiLineString
   * tile feature, so a single click can capture disconnected segments. Narrow a
   * clicked trail to just the segment nearest the click — you only select what
   * you clicked on (click again for a separate segment).
   */
  nearestTrailSegment(
    geometry: GeoJSON.Geometry,
    lngLat: { lng: number; lat: number }
  ): GeoJSON.Geometry {
    if (geometry.type !== 'MultiLineString' || geometry.coordinates.length <= 1) return geometry
    const pt = turfPoint([lngLat.lng, lngLat.lat])
    let best = geometry.coordinates[0]
    let bestD = Infinity
    for (const coords of geometry.coordinates) {
      if (coords.length < 2) continue
      let d: number
      try {
        d = pointToLineDistance(pt, lineString(coords))
      } catch {
        continue
      }
      if (d < bestD) {
        bestD = d
        best = coords
      }
    }
    return { type: 'LineString', coordinates: best }
  }

  /**
   * The constituent LineStrings of a (possibly merged) trail geometry that
   * actually intersect the box. A merged MultiLineString bundles disconnected
   * trails, so we test each component line individually and keep only those in
   * the box — covering named and unnamed trails alike, purely by geometry.
   */
  trailSegmentsInBox(
    geometry: GeoJSON.Geometry,
    box: { minLng: number; minLat: number; maxLng: number; maxLat: number }
  ): GeoJSON.Geometry[] {
    const lines =
      geometry.type === 'LineString'
        ? [geometry.coordinates]
        : geometry.type === 'MultiLineString'
          ? geometry.coordinates
          : []
    if (lines.length === 0) return []
    let poly: ReturnType<typeof bboxPolygon>
    try {
      poly = bboxPolygon([box.minLng, box.minLat, box.maxLng, box.maxLat])
    } catch {
      return []
    }
    const out: GeoJSON.Geometry[] = []
    for (const coords of lines) {
      if (coords.length < 2) continue
      let intersects = false
      try {
        intersects = !booleanDisjoint(lineString(coords), poly)
      } catch {
        intersects = false
      }
      if (intersects) out.push({ type: 'LineString', coordinates: coords })
    }
    return out
  }

  /** Render the captured geometry of selected backcountry features. */
  highlightSelection(refs: Array<BackcountryTrailRef | BackcountryPOIRef>): void {
    if (!this.map) return
    this.ensureSelectionLayers()
    const features: GeoJSON.Feature[] = refs.map(r => ({
      type: 'Feature',
      geometry: r.geometry,
      properties: {},
    }))
    const src = this.map.getSource<maplibregl.GeoJSONSource>(SELECTION_SOURCE_ID)
    src?.setData({ type: 'FeatureCollection', features })
  }

  clearSelectionHighlight(): void {
    const src = this.map?.getSource<maplibregl.GeoJSONSource>(SELECTION_SOURCE_ID)
    src?.setData({ type: 'FeatureCollection', features: [] })
  }

  private ensureSelectionLayers(): void {
    if (!this.map) return
    if (!this.map.getSource(SELECTION_SOURCE_ID)) {
      this.map.addSource(SELECTION_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!this.map.getLayer(SELECTION_LINE_LAYER)) {
      this.map.addLayer({
        id: SELECTION_LINE_LAYER,
        type: 'line',
        source: SELECTION_SOURCE_ID,
        filter: ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#3b82f6', 'line-width': 7, 'line-opacity': 0.5 },
      })
    }
    if (!this.map.getLayer(SELECTION_POINT_LAYER)) {
      this.map.addLayer({
        id: SELECTION_POINT_LAYER,
        type: 'circle',
        source: SELECTION_SOURCE_ID,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 11,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.3,
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-width': 2,
        },
      })
    }
  }

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map || !this._enabled) return []
    const queryLayers = allLayerIds().filter(id => this.map!.getLayer(id))
    if (queryLayers.length === 0) return []
    // Trails render as thin lines; query a small tolerance box around the click
    // (not a single pixel) so they register reliably, like a wider hit layer.
    const R = 6
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
      [point.x - R, point.y - R],
      [point.x + R, point.y + R],
    ]
    const hits = this.map.queryRenderedFeatures(box, { layers: queryLayers })
    if (hits.length === 0) return hits

    // Trail geometry carries no name in this schema — resolve it from the
    // transportation_name source-layer and attach it to the feature.
    const clickLngLat = this.map.unproject(point)
    for (const f of hits) {
      if (f.layer.id !== TRAILS_LAYER || f.properties.name) continue
      // queryRenderedFeatures returns fresh feature copies, so mutating the
      // properties bag here is safe and doesn't touch the source data.
      const name = this.resolveTrailName(f, clickLngLat)
      if (name) f.properties.name = name
    }
    return hits
  }

  /**
   * Look up a trail's name from the `transportation_name` source-layer (which
   * holds names the `transportation` geometry layer omits). Matches on the
   * shared OSM feature id first, then falls back to the nearest named line
   * within roughly the tap tolerance. Uses querySourceFeatures so it sees all
   * loaded features, not just the labels MapLibre happens to have rendered.
   */
  private resolveTrailName(
    trail: maplibregl.MapGeoJSONFeature,
    clickLngLat: maplibregl.LngLat
  ): string | undefined {
    if (!this.map) return undefined
    let nameFeatures: maplibregl.GeoJSONFeature[]
    try {
      nameFeatures = this.map.querySourceFeatures(SOURCE_ID, {
        sourceLayer: TRAIL_NAME_SOURCE_LAYER,
      })
    } catch {
      return undefined
    }
    if (nameFeatures.length === 0) return undefined

    // 1) Exact match on the shared OSM feature id, when present.
    if (trail.id != null) {
      const byId = nameFeatures.find(
        n => n.id === trail.id && typeof n.properties.name === 'string' && n.properties.name
      )
      if (byId) return byId.properties.name as string
    }

    // 2) Nearest named line within ~12px of the tap (zoom-scaled).
    const z = this.map.getZoom()
    const metersPerPixel =
      (156543.03392 * Math.cos((clickLngLat.lat * Math.PI) / 180)) / Math.pow(2, z)
    const maxDistM = Math.max(metersPerPixel * 12, 5)
    const from: [number, number] = [clickLngLat.lng, clickLngLat.lat]

    let best: { dist: number; name: string } | undefined
    for (const n of nameFeatures) {
      const name = n.properties.name
      if (typeof name !== 'string' || !name) continue
      const geom = n.geometry
      const lines: number[][][] =
        geom.type === 'LineString'
          ? [geom.coordinates]
          : geom.type === 'MultiLineString'
            ? geom.coordinates
            : []
      for (const coords of lines) {
        if (coords.length < 2) continue
        let d: number
        try {
          d = pointToLineDistance(
            from,
            { type: 'LineString', coordinates: coords },
            {
              units: 'meters',
            }
          )
        } catch {
          continue
        }
        if (d <= maxDistM && (!best || d < best.dist)) best = { dist: d, name }
      }
    }
    return best?.name
  }

  buildPopupHtml(features: maplibregl.MapGeoJSONFeature[]): string | null {
    if (features.length === 0) return null
    const seen = new Set<string>()
    const parts: string[] = []

    for (const f of features) {
      const p = f.properties
      const layerId = f.layer.id
      const key = `${layerId}:${p.name ?? ''}:${p.ele ?? ''}:${p.subclass ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)

      if (layerId === TRAILS_LAYER || layerId === TRAIL_LABELS_LAYER) {
        const name = p.name ?? p.ref ?? 'Unnamed trail'
        const sub = p.subclass ?? p.class ?? ''
        const surface = p.surface ? ` · ${p.surface}` : ''
        parts.push(
          `<div style="margin-bottom:6px">` +
            `<strong>${name}</strong>` +
            (sub ? `<br><em>${sub}${surface}</em>` : '') +
            `</div>`
        )
      } else if (layerId === PEAKS_LAYER || layerId === PEAK_LABELS_LAYER) {
        const name = p.name ?? 'Unnamed peak'
        const eleFt = p.ele_ft ? `${Math.round(Number(p.ele_ft)).toLocaleString()} ft` : ''
        const eleM = p.ele ? `${Math.round(Number(p.ele)).toLocaleString()} m` : ''
        const elev = eleFt && eleM ? `${eleFt} / ${eleM}` : eleFt || eleM
        parts.push(
          `<div style="margin-bottom:6px">` +
            `<strong>${name}</strong>` +
            (elev ? `<br>${elev}` : '') +
            `</div>`
        )
      } else if (layerId === SHELTERS_LAYER) {
        const name = p.name ?? 'Shelter'
        const sub = p.subclass?.replace(/_/g, ' ') ?? ''
        parts.push(
          `<div style="margin-bottom:6px">` +
            `<strong>${name}</strong>` +
            (sub ? `<br><em>${sub}</em>` : '') +
            `</div>`
        )
      } else if (layerId === CAMPSITES_LAYER) {
        const name = p.name ?? 'Campsite'
        parts.push(`<div style="margin-bottom:6px"><strong>${name}</strong></div>`)
      } else if (layerId === WATER_LAYER) {
        const name = p.name ?? 'Water source'
        const sub = p.subclass?.replace(/_/g, ' ') ?? ''
        parts.push(
          `<div style="margin-bottom:6px">` +
            `<strong>${name}</strong>` +
            (sub ? `<br><em>${sub}</em>` : '') +
            `</div>`
        )
      } else if (layerId === PASSES_LAYER || layerId === PASS_LABELS_LAYER) {
        const name = p.name ?? 'Unnamed pass'
        const eleM = p.ele ? `${Math.round(Number(p.ele)).toLocaleString()} m` : ''
        parts.push(
          `<div style="margin-bottom:6px">` +
            `<strong>${name}</strong>` +
            (eleM ? `<br>${eleM}` : '') +
            `</div>`
        )
      } else if (layerId === TRAILHEADS_LAYER) {
        const name = p.name ?? (p.subclass === 'parking' ? 'Parking' : 'Trailhead')
        parts.push(`<div style="margin-bottom:6px"><strong>${name}</strong></div>`)
      } else if (layerId === VIEWPOINTS_LAYER) {
        const name = p.name ?? 'Viewpoint'
        parts.push(`<div style="margin-bottom:6px"><strong>${name}</strong></div>`)
      } else if (layerId === FACILITIES_LAYER) {
        const name = p.name ?? p.subclass?.replace(/_/g, ' ') ?? 'Facility'
        parts.push(`<div style="margin-bottom:6px"><strong>${name}</strong></div>`)
      } else if (layerId === WATERFALLS_LAYER) {
        const name = p.name ?? 'Waterfall'
        parts.push(`<div style="margin-bottom:6px"><strong>${name}</strong></div>`)
      }
    }

    if (parts.length === 0) return null
    return `<div class="cairn-backcountry-popup">${parts.join('')}</div>`
  }

  // ── Layer builders (called by FEATURE_DEFS, must be public) ──

  addTrailLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getLayer(TRAILS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAILS_LAYER,
          type: 'line',
          source: SOURCE_ID,
          'source-layer': 'transportation',
          minzoom: 5,
          filter: [
            'all',
            ['match', ['get', 'class'], ['path', 'track'], true, false],
            ['!=', ['get', 'brunnel'], 'tunnel'],
          ],
          paint: {
            'line-color': '#E91E63',
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              5,
              0.3,
              8,
              0.5,
              10,
              1,
              14,
              2.5,
              16,
              3.5,
            ],
            'line-dasharray': [2, 1.5],
            'line-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(TRAIL_LABELS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAIL_LABELS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'transportation_name',
          minzoom: 9,
          filter: [
            'all',
            ['match', ['get', 'class'], ['path', 'track'], true, false],
            ['has', 'name'],
          ],
          layout: {
            'symbol-placement': 'line',
            'text-field': ['coalesce', ['get', 'name'], ['get', 'ref']],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 9, 9, 13, 11, 16, 13],
            'text-max-angle': 30,
            'text-padding': 5,
          },
          paint: {
            'text-color': '#E91E63',
            'text-halo-color': 'rgba(255,255,255,0.85)',
            'text-halo-width': 1.5,
            'text-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addPeakLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getLayer(PEAKS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: PEAKS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'mountain_peak',
          minzoom: 5,
          filter: [
            'all',
            ['==', ['get', 'class'], 'peak'],
            ['<=', ['coalesce', ['get', 'rank'], 99], 3],
          ],
          layout: {
            'icon-image': 'backcountry-icon-peaks',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.4, 8, 0.6, 12, 0.85, 14, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(PEAK_LABELS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: PEAK_LABELS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'mountain_peak',
          minzoom: 7,
          filter: [
            'all',
            ['==', ['get', 'class'], 'peak'],
            ['<=', ['coalesce', ['get', 'rank'], 99], 3],
            ['has', 'name'],
          ],
          layout: {
            'text-field': [
              'case',
              ['has', 'ele_ft'],
              ['concat', ['get', 'name'], '\n', ['to-string', ['round', ['get', 'ele_ft']]], "'"],
              ['get', 'name'],
            ],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-offset': [0, 1.4] as [number, number],
            'text-anchor': 'top',
            'text-optional': true,
          },
          paint: {
            'text-color': '#FF6F00',
            'text-halo-color': 'rgba(255,255,255,0.85)',
            'text-halo-width': 1.5,
            'text-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addShelterLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(SHELTERS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: SHELTERS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 7,
          filter: [
            'match',
            ['get', 'subclass'],
            ['shelter', 'alpine_hut', 'wilderness_hut'],
            true,
            false,
          ],
          layout: {
            'icon-image': 'backcountry-icon-shelters',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.4, 10, 0.6, 13, 0.85, 15, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addCampsiteLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(CAMPSITES_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CAMPSITES_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 7,
          filter: ['match', ['get', 'subclass'], ['camp_site', 'caravan_site'], true, false],
          layout: {
            'icon-image': 'backcountry-icon-campsites',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.4, 10, 0.6, 13, 0.85, 15, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addWaterLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(WATER_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: WATER_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 8,
          filter: [
            'match',
            ['get', 'subclass'],
            ['drinking_water', 'spring', 'water_well'],
            true,
            false,
          ],
          layout: {
            'icon-image': 'backcountry-icon-water',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.4, 11, 0.6, 13, 0.85, 15, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addPassLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getLayer(PASSES_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: PASSES_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'mountain_peak',
          minzoom: 7,
          filter: ['==', ['get', 'class'], 'saddle'],
          layout: {
            'icon-image': 'backcountry-icon-passes',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 7, 0.4, 10, 0.6, 13, 0.85, 15, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(PASS_LABELS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: PASS_LABELS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'mountain_peak',
          minzoom: 9,
          filter: ['all', ['==', ['get', 'class'], 'saddle'], ['has', 'name']],
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-offset': [0, 1.4] as [number, number],
            'text-anchor': 'top',
            'text-optional': true,
          },
          paint: {
            'text-color': '#7C3AED',
            'text-halo-color': 'rgba(255,255,255,0.85)',
            'text-halo-width': 1.5,
            'text-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addTrailheadLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(TRAILHEADS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: TRAILHEADS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 8,
          filter: ['match', ['get', 'subclass'], ['trailhead', 'parking'], true, false],
          layout: {
            'icon-image': 'backcountry-icon-trailheads',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.4, 11, 0.6, 13, 0.85, 15, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addViewpointLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(VIEWPOINTS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: VIEWPOINTS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 9,
          filter: ['==', ['get', 'subclass'], 'viewpoint'],
          layout: {
            'icon-image': 'backcountry-icon-viewpoints',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.4, 12, 0.6, 14, 0.85, 16, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addFacilityLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(FACILITIES_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: FACILITIES_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 10,
          filter: [
            'match',
            ['get', 'subclass'],
            ['ranger_station', 'information', 'toilets', 'picnic_site'],
            true,
            false,
          ],
          layout: {
            'icon-image': 'backcountry-icon-facilities',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.4, 13, 0.6, 15, 0.85, 17, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  addWaterfallLayers(): void {
    if (!this.map) return
    if (!this.map.getLayer(WATERFALLS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: WATERFALLS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          'source-layer': 'poi',
          minzoom: 9,
          filter: ['==', ['get', 'subclass'], 'waterfall'],
          layout: {
            'icon-image': 'backcountry-icon-waterfalls',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.4, 12, 0.6, 14, 0.85, 16, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
          paint: {
            'icon-opacity': this._opacity,
          },
        },
        GROUP_ID
      )
    }
  }

  // ── Private helpers ──

  private registerIcons(): void {
    if (!this.map) return
    const iconPromises = Object.entries(BACKCOUNTRY_ICONS).map(([key, { icon, color }]) =>
      registerBackcountryIcon(this.map!, `backcountry-icon-${key}`, icon, color)
    )
    this.iconsReady = Promise.all(iconPromises).then(() => {})
  }

  private ensureSource(): void {
    if (!this.map) return
    this.ensureGlyphs()
    if (!this.map.getSource(SOURCE_ID)) {
      if (OFM_PINNED_TILE_URL) {
        this.map.addSource(SOURCE_ID, {
          type: 'vector',
          tiles: [OFM_PINNED_TILE_URL],
          maxzoom: OFM_MAXZOOM,
        })
      } else {
        this.map.addSource(SOURCE_ID, {
          type: 'vector',
          url: OFM_TILEJSON_URL,
        })
      }
    }
  }

  private removeTypeLayers(type: BackcountryFeatureType): void {
    if (!this.map) return
    for (const id of FEATURE_DEFS[type].layers) {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    }
    this.removeSourceIfEmpty()
  }

  private removeAllLayers(): void {
    if (!this.map) return
    for (const id of allLayerIds().reverse()) {
      if (this.map.getLayer(id)) this.map.removeLayer(id)
    }
  }

  private removeSource(): void {
    if (!this.map) return
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  private removeSourceIfEmpty(): void {
    if (!this.map) return
    const hasLayers = allLayerIds().some(id => this.map!.getLayer(id))
    if (!hasLayers) this.removeSource()
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }
}

export const backcountryOverlayService = new BackcountryOverlayService()
